import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import UserManagement from './components/UserManagement';
import Navbar from './components/Navbar';
import { Home, AwardCheck, CourseProfitability, ConnectTeamScheduler, PrerequisiteManagement, PrerequisiteManagementFormula, Attendance, TempCards, TestSheetSubmissions, DOBVerifier, RoadClosures, CompanyPolicies, LSSInvoices } from './pages';
// Deprecated modules:
// import { CourseScraper, CourseUpload } from './pages';
// import ObjectPurgeSimple from './pages/ObjectPurgeSimple';
import ChangePassword from './components/ChangePassword';

// Import global border radius overrides
import './styles/global-overrides.css';
import { injectRadiusVariables } from './styles/radius-config';

// Initialize CSS custom properties for border radius
injectRadiusVariables();

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    success: {
      main: '#4caf50',
    },
  },
  shape: {
    borderRadius: 12, // Increased from default 4px to 12px
  },
  components: {
    // Override Material-UI component defaults
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
  },
});

// Main app container that preserves state between route changes
const MainAppContainer = () => {
  const location = useLocation();
  
  // Track which apps have been visited to preserve their state
  const [visitedApps, setVisitedApps] = useState(new Set());

  const currentPath = location.pathname;
  const isMainApp = ['/award-check', '/attendance', '/course-profitability', '/connecteam-scheduler', '/temp-cards', '/test-sheet-submissions', '/dob-verifier', '/road-closures', '/company-policies', '/lss-invoices'].includes(currentPath);

  // Add app to visited list when user navigates to it
  useEffect(() => {
    if (isMainApp) {
      setVisitedApps(prev => new Set([...prev, currentPath]));
    }
  }, [currentPath, isMainApp]);

  // Helper to determine if an app should be rendered (visited at least once)
  const shouldRenderApp = (appPath) => visitedApps.has(appPath);
  
  // Helper to determine if an app should be visible
  const isAppVisible = (appPath) => currentPath === appPath;

  if (!isMainApp) {
    // For non-main app routes (home, admin, etc.), render normally
    return null;
  }

  return (
    <div>
      {/* Award Check App */}
      {shouldRenderApp('/award-check') && (
        <div style={{ display: isAppVisible('/award-check') ? 'block' : 'none' }}>
          <AwardCheck />
        </div>
      )}
      
      {/* Attendance App */}
      {shouldRenderApp('/attendance') && (
        <div style={{ display: isAppVisible('/attendance') ? 'block' : 'none' }}>
          <Attendance />
        </div>
      )}
      
      {/* Deprecated: Verify Posted Courses / Course Scraper App
      {shouldRenderApp('/course-scraper') && (
        <div style={{ display: isAppVisible('/course-scraper') ? 'block' : 'none' }}>
          <CourseScraper />
        </div>
      )}
      */}
      
      {/* Deprecated: Course Builder / Course Upload App
      {shouldRenderApp('/course-upload') && (
        <div style={{ display: isAppVisible('/course-upload') ? 'block' : 'none' }}>
          <CourseUpload />
        </div>
      )}
      */}

      {/* Course Profitability App */}
      {shouldRenderApp('/course-profitability') && (
        <div style={{ display: isAppVisible('/course-profitability') ? 'block' : 'none' }}>
          <CourseProfitability />
        </div>
      )}
      
      {/* ConnectTeam Scheduler App */}
      {shouldRenderApp('/connecteam-scheduler') && (
        <div style={{ display: isAppVisible('/connecteam-scheduler') ? 'block' : 'none' }}>
          <ConnectTeamScheduler />
        </div>
      )}
      
      {/* Temp Cards App */}
      {shouldRenderApp('/temp-cards') && (
        <div style={{ display: isAppVisible('/temp-cards') ? 'block' : 'none' }}>
          <TempCards />
        </div>
      )}
      
      {/* Test Sheet Submissions App */}
      {shouldRenderApp('/test-sheet-submissions') && (
        <div style={{ display: isAppVisible('/test-sheet-submissions') ? 'block' : 'none' }}>
          <TestSheetSubmissions />
        </div>
      )}
      
      {/* DOB Verifier App */}
      {shouldRenderApp('/dob-verifier') && (
        <div style={{ display: isAppVisible('/dob-verifier') ? 'block' : 'none' }}>
          <DOBVerifier />
        </div>
      )}
      
      {/* Deprecated: Object Purge App
      {shouldRenderApp('/object-purge') && (
        <div style={{ display: isAppVisible('/object-purge') ? 'block' : 'none' }}>
          <ObjectPurgeSimple />
        </div>
      )}
      */}

      {/* Road Closures App */}
      {shouldRenderApp('/road-closures') && (
        <div style={{ display: isAppVisible('/road-closures') ? 'block' : 'none' }}>
          <RoadClosures />
        </div>
      )}

      {/* Company Policies App */}
      {shouldRenderApp('/company-policies') && (
        <div style={{ display: isAppVisible('/company-policies') ? 'block' : 'none' }}>
          <CompanyPolicies />
        </div>
      )}

      {/* LSS Invoices App */}
      {shouldRenderApp('/lss-invoices') && (
        <div style={{ display: isAppVisible('/lss-invoices') ? 'block' : 'none' }}>
          <LSSInvoices />
        </div>
      )}
    </div>
  );
};

// Component to handle routing and conditional rendering
const AppContainer = () => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      <Navbar />
      <Routes>
        <Route path="/login" element={<Login />} />
        
        {/* Home Route */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        
        {/* Main App Routes - these will use state preservation */}
        <Route
          path="/award-check"
          element={
            <ProtectedRoute requiredPermissions={['view_prerequisites', 'manage_prerequisites']}>
              <MainAppContainer />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/attendance"
          element={
            <ProtectedRoute requiredPermissions={['view_attendance', 'manage_attendance']}>
              <MainAppContainer />
            </ProtectedRoute>
          }
        />
        
        {/* Deprecated: Verify Posted Courses / Course Scraper route
        <Route
          path="/course-scraper"
          element={
            <ProtectedRoute requiredPermissions={['view_courses', 'manage_courses']}>
              <MainAppContainer />
            </ProtectedRoute>
          }
        />
        */}
        
        {/* Deprecated: Course Builder route
        <Route
          path="/course-upload"
          element={
            <ProtectedRoute requiredPermissions={['view_course_builder', 'manage_course_builder']}>
              <MainAppContainer />
            </ProtectedRoute>
          }
        />
        */}

        <Route
          path="/course-profitability"
          element={
            <ProtectedRoute requiredPermissions={['view_course_builder', 'manage_course_builder']}>
              <MainAppContainer />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/connecteam-scheduler"
          element={
            <ProtectedRoute requiredPermissions={['view_course_builder', 'manage_course_builder']}>
              <MainAppContainer />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/temp-cards"
          element={
            <ProtectedRoute requiredPermissions={['view_temp_cards', 'manage_temp_cards']}>
              <MainAppContainer />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/test-sheet-submissions"
          element={
            <ProtectedRoute requiredPermissions={['view_test_sheet_submissions', 'manage_test_sheet_submissions']}>
              <MainAppContainer />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/dob-verifier"
          element={
            <ProtectedRoute requiredPermissions={['view_dob_verifier', 'manage_dob_verifier']}>
              <MainAppContainer />
            </ProtectedRoute>
          }
        />
        
        {/* Deprecated: Object Purge route
        <Route
          path="/object-purge"
          element={
            <ProtectedRoute requiredPermission="manage_users">
              <MainAppContainer />
            </ProtectedRoute>
          }
        />
        */}

        <Route
          path="/road-closures"
          element={
            <ProtectedRoute requiredPermission="manage_users">
              <MainAppContainer />
            </ProtectedRoute>
          }
        />

        <Route
          path="/company-policies"
          element={
            <ProtectedRoute requiredPermission="manage_users">
              <MainAppContainer />
            </ProtectedRoute>
          }
        />

        <Route
          path="/lss-invoices"
          element={
            <ProtectedRoute requireAdmin>
              <MainAppContainer />
            </ProtectedRoute>
          }
        />
        
        {/* Admin Routes */}
        <Route
          path="/users"
          element={
            <ProtectedRoute requiredPermission="manage_users">
              <UserManagement />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/prerequisites"
          element={
            <ProtectedRoute requiredPermission="manage_prerequisites">
              <PrerequisiteManagementFormula />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/prerequisites-old"
          element={
            <ProtectedRoute requiredPermission="manage_prerequisites">
              <PrerequisiteManagement />
            </ProtectedRoute>
          }
        />
        
        {/* Account Route */}
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          }
        />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      
      <div 
        style={{
          position: 'fixed',
          bottom: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#888',
          fontStyle: 'italic',
          fontSize: isHovered ? '14px' : '12px',
          userSelect: isHovered ? 'text' : 'none',
          opacity: isHovered ? 0.7 : 0,
          transition: 'opacity 0.3s ease, color 0.3s ease, font-size 0.3s ease',
          textAlign: 'center',
          width: '100%'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        Dedicated to little Cristina ♥
      </div>
    </div>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <Router>
          <AppContainer />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
